import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Form,
  Modal,
  ModalProps,
  RawInput,
  useToastContext,
} from '@payaca/ambrosia';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';

import useSetProposalCustomReference from '@/api/mutations/proposals/useSetProposalCustomReference';
import { useGetProposal } from '@/api/queries/proposal/useGetProposal';

// `jobs.customReference` is a varchar(25) column, so the server caps the length
// at 25 and stores a blank value as null.
const editReferenceSchema = z.object({
  customReference: z
    .string()
    .trim()
    .max(25, 'Reference must be 25 characters or fewer'),
});

type TFormValues = z.infer<typeof editReferenceSchema>;

const EditReferenceModal: FC<
  {
    /** Public id of the Proposal being edited. */
    proposalId: string;
  } & Omit<ModalProps, 'title' | 'children'>
> = (props) => {
  const { proposalId, isOpen, onClose, ...rest } = props;

  const { proposal } = useGetProposal(proposalId);
  const { pushSuccess } = useToastContext();

  const formMethods = useForm<TFormValues>({
    defaultValues: { customReference: proposal?.customReference ?? '' },
    resolver: zodResolver(editReferenceSchema),
  });

  const {
    register,
    reset,
    formState: { isDirty },
  } = formMethods;

  const { mutate: setCustomReference, isPending } =
    useSetProposalCustomReference({
      proposalId,
      options: {
        onSuccess: () => {
          pushSuccess('Reference updated');
          onClose?.();
        },
      },
    });

  // Refresh the form to the latest reference whenever the modal opens — the
  // Modal stays mounted for its close animation, so defaultValues alone won't
  // pick up a subsequent change.
  useEffect(() => {
    if (isOpen) {
      reset({ customReference: proposal?.customReference ?? '' });
    }
  }, [isOpen, proposal?.customReference, reset]);

  // A blank reference clears the custom reference and falls back to the
  // auto-assigned numeric reference.
  const handleSubmit = (values: TFormValues) => {
    setCustomReference({
      proposalId,
      customReference: values.customReference || null,
    });
  };

  return (
    <Modal title="Edit reference" isOpen={isOpen} onClose={onClose} {...rest}>
      <Form methods={formMethods} onSubmit={handleSubmit}>
        <Modal.Body>
          <Field name="customReference">
            <Field.Label>Reference</Field.Label>
            <Field.HelperText>
              Leave blank to use the default reference
              {proposal?.reference != null ? ` (#${proposal.reference})` : ''}.
            </Field.HelperText>
            <RawInput
              {...register('customReference')}
              placeholder={`#${proposal?.reference.toString()}`}
            />
          </Field>
        </Modal.Body>

        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button variant="secondary" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              isProcessing={isPending}
              disabled={isPending || !isDirty}
            >
              Save
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditReferenceModal;
